/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import { useEffect, useState } from "react";
import Select from 'react-select';
import MarkdownText from "components/common/MarkdownText";
import SectionElement from './SectionElement';
import Race from "model/character/Race";
import { getDocKey, sortSourceGroups } from "model/formats/Open5eDocument";
import React from "react";


interface Option {
  label: string;
  value: Race;
}

interface RacePageProps {
  races: Race[];
  selectedRace: Race | null;
  onRaceChange: (race: Race | null) => void;
  documentSources: Map<string, { name: string; licenses: string[]; }>;
  optionsSelectedArrayByOptionNameMap: Map<string, any>; // Adjust the type based on usage
  onSetOption: (optionName: string, value: any) => void;
}

const RacePage: React.FC<RacePageProps> = ({
  races,
  selectedRace,
  onRaceChange,
  documentSources,
  optionsSelectedArrayByOptionNameMap,
  onSetOption
}) => {
  const [options, setOptions] = useState<{label: string, options: Option[]}[]>([]);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  useEffect(() => {
    if (races && documentSources) {
      const racesBySource: Map<string, Option[]> = races.reduce((acc, race) => {
        const key = getDocKey(race);
        const value = acc.get(key) || [];
        value.push({ value: race, label: race.name });
        acc.set(key, value);
        return acc;
      }, new Map<string, Option[]>());

      const formattedOptions: {label: string, options: Option[]}[] = Array.from(documentSources, ([key, license]) => {
        const raceOptions = racesBySource.get(key) || [];
        return { label: license.name, options: raceOptions.sort((a, b) => a.label.localeCompare(b.label)) };
      });

      setOptions(sortSourceGroups(formattedOptions));

      // Update selectedOption based on selectedRace
      if (selectedRace) {
        const match = formattedOptions.flatMap(group => group.options)
                                      .find(option => option.value === selectedRace);
        if (match) {
          setSelectedOption(match);
        }
      }
    }
  }, [races, documentSources, selectedRace]);

  const handleRaceChange = (option: Option | null) => {
    setSelectedOption(option);
    onRaceChange(option?.value || null);
  };

  const customStyles = {
    groupHeading: (base: any) => ({
      ...base,
      backgroundColor: '#f2f2f2',
      color: '#333',
      paddingLeft: '10px',
      padding: '5px',
      fontSize: '26px',
      fontWeight: 'bold',
      textTransform: 'none'
    }),
    option: (styles: any, { isFocused, isSelected }: any) => ({
      ...styles,
      paddingLeft: '40px',
      paddingTop: '0px',
      paddingBottom: '0px',
      backgroundColor: isFocused ? 'lightgray' : 'white',
      color: 'black',
      fontWeight: isSelected ? 'bold' : 'normal',
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (provided: any) => ({ ...provided, width: '350px', margin: '5px' })
  };

  const sections = selectedRace?.sections;
  return (
    <>
      <div className="select-container" style={{ width: '100%' }}>
        <b>Select Race:</b>&nbsp;
        <Select
          menuPortalTarget={document.body}
          styles={customStyles}
          options={options}
          value={selectedOption}
          onChange={handleRaceChange}
          placeholder="Select a race"
          isClearable
          isSearchable
        />
      </div>
      {selectedRace && (
        <>
          {sections?.map((section, index) => (
            <SectionElement
              key={index}
              section={section}
              index={index}
              optionsSelectedArrayByOptionNameMap={optionsSelectedArrayByOptionNameMap}
              onSetOption={onSetOption}
              showSelect={true}
            />
          ))}
          <br /><MarkdownText text={selectedRace.desc || ''} mon={null} />
          <br /><MarkdownText text={selectedRace.asi_desc || ''} mon={null} />
          <br /><MarkdownText text={selectedRace.age || ''} mon={null} />
          <br /><MarkdownText text={selectedRace.alignment || ''} mon={null} />
          <br /><MarkdownText text={selectedRace.size || ''} mon={null} />
          <br /><MarkdownText text={selectedRace.speed_desc || ''} mon={null} />
          <br /><MarkdownText text={selectedRace.languages || ''} mon={null} />
          <br /><MarkdownText text={selectedRace.vision || ''} mon={null} />
          <hr />
          {sections?.map((section, index) => (
            <SectionElement
              key={index}
              section={section}
              index={index}
              optionsSelectedArrayByOptionNameMap={optionsSelectedArrayByOptionNameMap}
              onSetOption={onSetOption}
              showSelect={false}
            />
          ))}
        </>
      )}
    </>
  );
};

export default RacePage;
